import React, { useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getAdminFees, patchAdminFees } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import {
  AdminFeesPanel,
  defaultFeeConfig,
  type FeeConfig,
} from '../components/admin/AdminFeesPanel.tsx';

export function AdminFeesSettings() {
  const { user, loading: authLoading } = useAuth();
  const [saved, setSaved] = useState<FeeConfig>(defaultFeeConfig);
  const [draft, setDraft] = useState<FeeConfig>(defaultFeeConfig);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!user) return;
      const resp = await getAdminFees();
      const config = {
        ...defaultFeeConfig,
        ...(resp.data as Record<string, unknown>),
      } as unknown as FeeConfig;
      setSaved(config);
      setDraft(config);
    };
    if (!authLoading) {
      void load().catch((e) => setError(e instanceof Error ? e.message : 'Failed to load platform fees'));
    }
  }, [user, authLoading]);

  const save = async () => {
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      await patchAdminFees(draft);
      setSaved(draft);
      setMessage('Platform defaults saved. Applies to new markets from now.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="flex items-center gap-4 mb-6">
          <Link to="/admin" className="text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <h1 className="text-3xl font-bold">Revenue & Fee Settings</h1>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-sm text-blue-900">
          These are the <strong>platform defaults</strong> applied to any new market at creation. To change fees
          for a specific market, open that market from{' '}
          <Link to="/admin/market-requests" className="underline font-medium">
            Market Requests
          </Link>{' '}
          and use the <strong>Fees</strong> tab there.
        </div>

        <AdminFeesPanel
          saved={saved}
          draft={draft}
          onChange={setDraft}
          onSave={save}
          saving={loading}
          errorMessage={error}
          successMessage={message}
          saveLabel="Save platform defaults"
        />
      </div>
    </div>
  );
}
